import React, { useEffect } from 'react';
import { useLocation } from "react-router";
import { cn } from '../../../styles/cva-v2';
import { 
  sidebar,
  sidebarHeader,
  sidebarContent,
  sidebarItem,
  sidebarOverlay,
  sidebarToggleButton,
  sidebarLogoContainer,
  sidebarTitle,
  sidebarIconContainer,
  sidebarItemText,
  sidebarActiveIndicator,
  type SidebarProps as CVASidebarProps,
  type SidebarItemProps as CVASidebarItemProps
} from '../../../styles/cva-v2/compositions/navigation';
import { NAVIGATION, APP } from '@buzunarul/shared-constants';
import { useNavigationState } from '../../../hooks/useNavigationState';
import Tooltip from '../Tooltip/Tooltip';
import { BuzunarLogo } from '../../shared/BuzunarLogo';

/**
 * 🎯 SIDEBAR REFACTORIZAT - CVA STYLING COMPLET
 * 
 * Toate aspectele de styling gestionate prin CVA:
 * ✅ Layout responsive (desktop/mobile)
 * ✅ States (expanded/collapsed/visible/hidden) 
 * ✅ Spacing și dimensiuni consistente
 * ✅ Dark mode support complet
 * ✅ Transitions și animații
 * ✅ Z-index management
 * 
 * ZERO styling manual - totul prin CVA variants!
 * 
 * 🔧 LOOP INFINIT FIX (2025-01-15):
 * Problem: useEffect dependency arrays cu `actions` și `onToggle` cauzau re-render infinit
 * Solution: 
 * - Eliminat `actions` din deps și verificat null-check în useEffect
 * - Eliminat `onToggle` din deps pentru a evita re-render la fiecare prop change
 * - Separat `headerState` de `sidebarState` pentru compatibilitate CVA types
 */

/**
 * Props pentru componenta Sidebar
 */
export interface SidebarProps {
  /** Conținutul sidebar-ului */
  children?: React.ReactNode;
  /** CSS classes adiționale */
  className?: string;
  /** Data test ID pentru testare */
  testId?: string;
  /** Callback pentru toggle state */
  onToggle?: (isExpanded: boolean) => void;
  /** Starea inițială (expanded/collapsed) */
  defaultExpanded?: boolean;
  /** Versiunea pentru mobile (hamburger menu) */
  variant?: 'desktop' | 'mobile';
  /** Overlay pentru mobile */
  showOverlay?: boolean;
  /** Callback pentru închiderea overlay-ului */
  onOverlayClick?: () => void;
}

/**
 * Props pentru SidebarItem
 */
export interface SidebarItemProps {
  /** Conținutul item-ului */
  children: React.ReactNode;
  /** URL-ul pentru navigare */
  to?: string;
  /** Icon (opțional) */
  icon?: React.ReactNode;
  /** CSS classes adiționale */
  className?: string;
  /** Data test ID pentru testare */
  testId?: string;
  /** Callback pentru click */
  onClick?: () => void;
  /** Indică dacă item-ul este activ */
  isActive?: boolean;
  /** Tooltip text pentru collapsed state */
  tooltip?: string;
  /** Variant for the item */
  variant?: CVASidebarItemProps['variant'];
  /** Size for the item */
  size?: CVASidebarItemProps['size'];
}

/**
 * 🎯 SIDEBAR ITEM - Simplificat cu CVA complet
 */
export const SidebarItem = ({
  children,
  to,
  icon,
  className,
  testId,
  onClick,
  isActive = false,
  variant = 'default',
  size = 'md',
  tooltip
}: SidebarItemProps) => {
  const location = useLocation();
  const { state } = useNavigationState() || {};
  
  const isActiveItem = isActive || (to && location.pathname === to);
  const isCollapsed = !(state?.sidebarExpanded ?? true);
  
  // 🔧 FIX: Track transition state
  const [isTransitioning, setIsTransitioning] = React.useState(false);
  const prevCollapsedRef = React.useRef(isCollapsed);
  
  React.useEffect(() => {
    // Detectează când se schimbă de la expanded la collapsed
    if (!prevCollapsedRef.current && isCollapsed) {
      setIsTransitioning(true);
      // Reset după durata animației
      const timer = setTimeout(() => {
        setIsTransitioning(false);
      }, 200); // Match cu durata transition-ului CSS
      
      return () => clearTimeout(timer);
    }
    prevCollapsedRef.current = isCollapsed;
  }, [isCollapsed]);
  
  // CVA variant bazat pe state
  const itemVariant = isActiveItem ? 'active' : variant;
  const itemState = isTransitioning ? 'transitioning' : (isCollapsed ? 'collapsed' : 'expanded');

  const content = (
    <>
      {icon && (
        <span className={cn(sidebarIconContainer({ 
          state: itemState === 'transitioning' ? 'collapsed' : itemState
        }))}>
          {icon}
        </span>
      )}
      {/* 🔧 FIX: Render text based on itemState */}
      {itemState === 'expanded' && children && (
        <span className={cn(sidebarItemText())}>
          {children}
        </span>
      )}
    </>
  );

  // ✅ CVA STYLING: Toate clasele gestionate prin CVA
  const itemClasses = cn(
    sidebarItem({ variant: itemVariant, state: itemState, size }),
    className
  );

  const itemElement = to ? (
    <a
      href={to}
      className={itemClasses}
      data-testid={testId}
      onClick={(e) => {
        e.preventDefault();
        window.history.pushState({}, '', to);
        window.dispatchEvent(new PopStateEvent('popstate'));
        onClick?.();
      }}
    >
      {content}
    </a>
  ) : (
    <button
      className={itemClasses}
      data-testid={testId}
      onClick={onClick}
    >
      {content}
    </button>
  );

  // Tooltip pentru collapsed state (dar nu în timpul tranziției)
  if (itemState === 'collapsed' && tooltip && !isTransitioning) {
    return (
      <Tooltip
        content={tooltip}
        side="right"
        align="center"
        sideOffset={8}
        variant="default"
        delay={500}
      >
        {itemElement}
      </Tooltip>
    );
  }

  return itemElement;
};

/**
 * 🎯 SIDEBAR PRINCIPAL - Complet refactorizat cu CVA
 */
const Sidebar = ({
  children,
  className,
  testId = 'sidebar',
  onToggle,
  defaultExpanded = true,
  variant = 'desktop',
  showOverlay = false,
  onOverlayClick,
}: SidebarProps) => {
  const { state, actions } = useNavigationState() || {};
  
  const isExpanded = state?.sidebarExpanded ?? true;
  const currentVariant = variant || state?.sidebarVariant || 'desktop';

  // 🔧 FIX: Sync variant cu navigation state - doar când se schimbă efectiv
  useEffect(() => {
    if (actions?.setSidebarVariant && currentVariant !== state?.sidebarVariant) {
      actions.setSidebarVariant(currentVariant);
    }
  }, [currentVariant, state?.sidebarVariant]); // 🔧 Eliminat actions din deps

  // 🔧 FIX: Sync defaultExpanded doar la mount - fără dependencies problematice
  useEffect(() => {
    if (actions?.setSidebarExpanded && defaultExpanded !== isExpanded) {
      actions.setSidebarExpanded(defaultExpanded);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 🔧 FIX: Notifică părintele doar când isExpanded se schimbă efectiv
  useEffect(() => {
    if (onToggle) {
      onToggle(isExpanded);
    }
  }, [isExpanded]); // 🔧 Eliminat onToggle din deps pentru a evita re-render

  const handleToggle = () => {
    actions?.toggleSidebar();
  };

  // ✅ CVA STATES: Gestionare state complet prin CVA
  const sidebarState = currentVariant === 'mobile' 
    ? (showOverlay ? 'visible' : 'hidden')
    : (isExpanded ? 'expanded' : 'collapsed');

  // 🔧 FIX: Header state pentru CVA - folosește doar expanded/collapsed
  const headerState = (currentVariant === 'mobile' || isExpanded) ? 'expanded' : 'collapsed';

  // Mobile variant
  if (currentVariant === 'mobile') {
    return (
      <>
        {/* ✅ CVA OVERLAY: Styling complet prin CVA */}
        <div
          className={cn(sidebarOverlay({ visible: showOverlay }))}
          onClick={onOverlayClick}
          data-testid={`${testId}-overlay`}
          aria-label={NAVIGATION.MOBILE.MENU_OVERLAY}
        />
        
        {/* ✅ CVA SIDEBAR: Mobile variant complet prin CVA */}
        <aside
          className={cn(
            sidebar({ variant: 'mobile', state: sidebarState }),
            className
          )}
          data-testid={testId}
          aria-label={NAVIGATION.ARIA.NAVIGATION}
        >
          {/* ✅ CVA HEADER: Header styling prin CVA */}
          <div className={cn(sidebarHeader({ state: headerState }))}>
            <h2 className={cn(sidebarTitle({ variant: 'subtitle' }))}>
              {NAVIGATION.ITEMS.DASHBOARD}
            </h2>
            <button
              onClick={onOverlayClick}
              className={cn(sidebarItem({ variant: 'ghost', state: 'expanded', size: 'sm' }))}
              data-testid={`${testId}-close-button`}
              aria-label={NAVIGATION.MOBILE.CLOSE_MENU}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* ✅ CVA CONTENT: Content styling prin CVA */}
          <nav className={cn(sidebarContent())}>
            {children}
          </nav>
        </aside>
      </>
    );
  }


  // Desktop variant
  return (
    <aside
      className={cn(
        // Base styles
        'relative z-40 h-screen',
        'bg-white dark:bg-carbon-900',
        'border-r border-carbon-200 dark:border-carbon-700',
        'flex flex-col',
        'overflow-hidden',
        
        // Smooth width transitions
        'transition-all duration-300 ease-in-out',
        
        // Width states cu animații smooth
        variant === 'desktop' && [
          state?.sidebarExpanded ? 'w-64' : 'w-16',
        ],
        
        variant === 'mobile' && [
          'fixed top-0 left-0',
          state?.sidebarExpanded ? 'w-64' : 'w-0',
        ],
        
        className
      )}
      data-testid={testId}
      data-variant={variant}
    >
      {/* Mobile overlay cu fade animation */}
      {variant === 'mobile' && state?.sidebarExpanded && (
        <div
          className={cn(
            "fixed inset-0 bg-black/50 z-[-1]",
            "transition-opacity duration-300 ease-in-out",
            "animate-in fade-in duration-300"
          )}
          onClick={() => actions?.toggleSidebar?.()}
          data-testid={`${testId}-overlay`}
        />
      )}

      {/* ✅ CVA HEADER: Header cu logo și toggle */}
      <div className={cn(sidebarHeader({ state: headerState }))}>
        {isExpanded ? (
          <>
            {/* Expanded: Logo + Title */}
            <div className="flex items-center gap-3">
              <div className={cn(sidebarLogoContainer({ state: 'expanded' }))}>
                <BuzunarLogo className="w-8 h-8" variant="icon" />
              </div>
              <h2 className={cn(sidebarTitle())}>
                Buzunarul
              </h2>
            </div>
            
            {/* Toggle button cu smooth rotation */}
            <button
              onClick={() => actions?.toggleSidebar?.()}
              className={cn(
                sidebarToggleButton({ state: state?.sidebarExpanded ? 'expanded' : 'collapsed' }),
                "transition-all duration-300 ease-in-out"
              )}
              data-testid={`${testId}-toggle`}
              aria-label={state?.sidebarExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
              aria-expanded={state?.sidebarExpanded}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={cn(
                  "transition-transform duration-300 ease-in-out",
                  !state?.sidebarExpanded && "rotate-180"
                )}
              >
                <path
                  d="M10 12L6 8L10 4"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </>
        ) : (
          <>
            {/* Collapsed: Logo centrat sus */}
            <Tooltip
              content={APP.NAME}
              side="right"
              align="center"
              sideOffset={8}
              variant="default"
              delay={500}
            >
              <div className={cn(sidebarLogoContainer({ state: 'collapsed' }))}>
                <BuzunarLogo className="w-8 h-8" variant="icon" />
              </div>
            </Tooltip>
            
            {/* Separator vizual */}
            <div className="w-8 h-px bg-carbon-200 dark:bg-carbon-700" />
            
            {/* Toggle button cu tooltip */}
            <Tooltip
              content="Expand sidebar"
              side="right"
              align="center"
              sideOffset={8}
              variant="default"
              delay={500}
            >
              <button
                onClick={() => actions?.toggleSidebar?.()}
                className={cn(
                  sidebarToggleButton({ state: state?.sidebarExpanded ? 'expanded' : 'collapsed' }),
                  "transition-all duration-300 ease-in-out"
                )}
                data-testid={`${testId}-toggle`}
                aria-label={state?.sidebarExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
                aria-expanded={state?.sidebarExpanded}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className={cn(
                    "transition-transform duration-300 ease-in-out",
                    !state?.sidebarExpanded && "rotate-180"
                  )}
                >
                  <path
                    d="M10 12L6 8L10 4"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </Tooltip>
          </>
        )}
      </div>
      
      {/* ✅ CVA CONTENT: Navigation content */}
      <nav 
        className={cn(sidebarContent({ state: isExpanded ? 'expanded' : 'collapsed' }))}
        key={`nav-${isExpanded}`}
      >
        {children}
      </nav>
    </aside>
  );
};

export default Sidebar; 
